import { useState, useEffect } from 'react';
import { X, Save, User } from 'lucide-react';
import apiService from '@/shared/services/api';
import { backendErrorMessage } from '@/shared/utils/apiError';

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  currentFullName: string;
  currentReceiveAlerts: boolean;
}

export default function EditProfileModal({
  isOpen,
  onClose,
  onSaved,
  currentFullName,
  currentReceiveAlerts,
}: EditProfileModalProps) {
  const [fullName, setFullName] = useState(currentFullName);
  const [receiveAlerts, setReceiveAlerts] = useState(currentReceiveAlerts);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);

  // Sync form values when modal opens
  useEffect(() => {
    if (isOpen) {
      setFullName(currentFullName);
      setReceiveAlerts(currentReceiveAlerts);
      setError(null);
    }
  }, [isOpen, currentFullName, currentReceiveAlerts]);

  const handleClose = () => {
    if (isLoading) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  };

  const handleSave = async () => {
    const trimmed = fullName.trim();

    if (!trimmed) {
      setError('El nombre completo es obligatorio.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.updateProfile({ fullName: trimmed, receiveAlerts });

      if (response.success) {
        setIsLoading(false);
        onSaved();
        handleClose();
      } else {
        setIsLoading(false);
        setError(response.error?.message || 'No se pudo actualizar el perfil.');
      }
    } catch (err) {
      setIsLoading(false);
      setError(backendErrorMessage(err) || 'Error de conexión. Intentá de nuevo.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-200 ${isClosing ? 'opacity-0' : 'opacity-50'
          }`}
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className={`relative bg-white rounded-xl shadow-2xl max-w-md w-full transform transition-all duration-200 ${isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
          }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Editar perfil</h3>
              <p className="text-sm text-gray-500">Actualizá tu nombre completo</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-danger-50 border border-danger-200 rounded-lg p-3">
              <p className="text-sm text-danger-700">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre completo
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              disabled={isLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-colors"
              placeholder="Tu nombre completo"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="receiveAlerts"
              checked={receiveAlerts}
              onChange={(e) => setReceiveAlerts(e.target.checked)}
              disabled={isLoading}
              className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
            />
            <label htmlFor="receiveAlerts" className="text-sm text-gray-700">
              Recibir alertas por email sobre vencimientos de guías
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Guardar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
